/**
 * InvitePage - Dedicated invite landing page
 * ============================================
 * Validates the invite code, shows who it's for, and provides Google sign-in.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const InvitePage = () => {
  const { code } = useParams();
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  const [status, setStatus] = useState('loading'); // loading | valid | invalid | used
  const [emailHint, setEmailHint] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Store invite code in localStorage for the OAuth flow
  useEffect(() => {
    if (code) {
      localStorage.setItem('invite_code', code);
    }
  }, [code]);

  // Validate the invite code on mount
  useEffect(() => {
    if (!code) {
      setStatus('invalid');
      setErrorMsg('No invite code provided.');
      return;
    }

    const validate = async () => {
      try {
        const res = await fetch(`${API}/api/invites/validate/${code}`);
        if (res.ok) {
          const data = await res.json();
          setStatus('valid');
          setEmailHint(data.email_hint || '');
        } else if (res.status === 410) {
          setStatus('used');
          setErrorMsg('This invite has already been used.');
        } else {
          setStatus('invalid');
          setErrorMsg('This invite link is invalid or expired.');
        }
      } catch {
        setStatus('invalid');
        setErrorMsg('Unable to verify invite. Please try again.');
      }
    };

    validate();
  }, [code]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Error messages from OAuth redirect
  const ERROR_MESSAGES = {
    invite_email_mismatch: 'This invite was generated for a different email. Please sign in with the correct Google account.',
    invite_only: 'Invalid or missing invite code. Please use the invite link sent to you.',
    google_auth_failed: 'Google authentication failed. Please try again.',
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="invite-page">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/3 right-1/3 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative w-full max-w-md" data-testid="invite-card">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <span className="text-white font-black text-xl tracking-tighter">AG</span>
            </div>
            <div>
              <span className="text-2xl font-bold text-white tracking-tight">AlgoGreeks</span>
              <span className="text-xs text-gray-500 ml-2">HFT Engine</span>
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-8" data-testid="invite-loading">
              <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-400 text-sm">Verifying your invite...</p>
            </div>
          )}

          {/* Valid Invite */}
          {status === 'valid' && (
            <div data-testid="invite-valid">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">You're Invited!</h2>
                <p className="text-gray-400 text-sm">
                  You've been invited to join AlgoGreeks HFT Engine
                </p>
              </div>

              {/* Email hint */}
              {emailHint && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 mb-6" data-testid="invite-email-hint">
                  <Mail className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Invite for</p>
                    <p className="text-sm text-cyan-300 font-mono font-medium">{emailHint}</p>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center mb-6">
                Sign in with the Google account matching the invited email address. This invite can only be used once.
              </p>

              {/* OAuth error from redirect */}
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-4" data-testid="invite-error">
                  {ERROR_MESSAGES[error] || `Error: ${error}`}
                </div>
              )}

              {/* Google Sign In Button */}
              <button
                onClick={loginWithGoogle}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl bg-white text-gray-900 font-semibold 
                         hover:bg-gray-100 transition-all duration-200 shadow-lg shadow-white/10"
                data-testid="invite-google-signin-btn"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign in with Google</span>
              </button>

              <div className="flex items-center gap-2 justify-center mt-4">
                <Shield className="w-3 h-3 text-gray-600" />
                <p className="text-[11px] text-gray-600">Secure authentication via Google OAuth 2.0</p>
              </div>
            </div>
          )}

          {/* Used Invite */}
          {status === 'used' && (
            <div className="text-center py-6" data-testid="invite-used">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invite Already Used</h2>
              <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
              <p className="text-gray-500 text-xs">
                If you already created an account, <a href="/login" className="text-cyan-400 hover:text-cyan-300 underline">sign in here</a>.
              </p>
            </div>
          )}

          {/* Invalid Invite */}
          {status === 'invalid' && (
            <div className="text-center py-6" data-testid="invite-invalid">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Invalid Invite</h2>
              <p className="text-gray-400 text-sm mb-6">{errorMsg}</p>
              <p className="text-gray-500 text-xs">
                Contact admin for a valid invite link, or <a href="/login" className="text-cyan-400 hover:text-cyan-300 underline">sign in</a> if you already have an account.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
